import Konva from "konva";
import { Context } from "konva/types/Context";
import { KonvaNodeComponent } from "react-konva";

interface HoverableProps extends Konva.RectConfig {
  hoverFill?: string;
  autoSize?: boolean;
  hoverChange?: (hover: boolean) => void;
  cursor?: string;
}

class Hoverable extends Konva.Rect {
  private _fill: string;
  private _hoverFill: string;
  private _autoSize: boolean;
  // @ts-expect-error _parent is assigned in the init() function.
  private _parent: Konva.Group;
  private _cursor?: string;
  private _onHoverChange: ((hover: boolean) => void) | undefined;
  private _mouseIn: boolean;

  constructor(config: HoverableProps) {
    super(config);
    this._hoverFill = config.hoverFill ?? "transparent";
    this._autoSize = config.autoSize ?? (config.width === undefined || config.height === undefined);
    this._fill = this.fill() ?? "transparent";
    this._onHoverChange = config.hoverChange;
    this._cursor = config.cursor;
    this._mouseIn = false;
    this.getSelfRect = () => ({ x: 0, y: 0, width: 0, height: 0 });
  }

  init() {
    // @ts-expect-error Konva parent type is wrong here.
    this._parent = this.getParent();
    if (this._parent) {
      const enter = this.onMouseEnter.bind(this);
      const leave = this.onMouseLeave.bind(this);
      this._parent.on("mouseenter", enter);
      this._parent.on("mouseleave", leave);
    }
  }

  // @ts-expect-error This will work.
  remove() {
    if (this._mouseIn && this._cursor) {
      // this logic will fail in there are overlapping Hoverables.
      // I'll need to keep track of mouse position and check if it's still in the hoverable
      this.getStage()?.container().style.setProperty("cursor", "default");
      this._mouseIn = false;
      this.getLayer()?.batchDraw();
    }
    super.remove();
  }

  onMouseEnter() {
    this.fill(this._hoverFill);
    this._mouseIn = true;
    this._onHoverChange && this._onHoverChange(true);
    this._cursor && this.getStage()?.container().style.setProperty("cursor", this._cursor);
    this.getLayer()?.batchDraw();
  }

  onMouseLeave() {
    this.fill(this._fill);
    this._mouseIn = false;
    this._onHoverChange && this._onHoverChange(false);
    this._cursor && this.getStage()?.container().style.setProperty("cursor", "default");
    this.getLayer()?.batchDraw();
  }

  _sceneFunc(context: Context) {
    super._sceneFunc(context);
    if (!this._parent) this.init();
    if (this._autoSize && this._parent) {
      // queue a resize check
      // never do this in the same frame as the render
      // @ts-expect-error RelativeTo stage is fine.
      const parentSize = this._parent.getClientRect({ relativeTo: this.getStage(), skipTransform: true });
      if (this.width() !== parentSize.width || this.height() !== parentSize.height) {
        setTimeout(() => {
          // @ts-expect-error RelativeTo stage is fine.
          const parentSize = this._parent.getClientRect({ relativeTo: this.getStage(), skipTransform: true });
          this.width(parentSize.width);
          this.height(parentSize.height);
          this.getLayer()?.batchDraw();
        });
      }
    }
  }
}

// @ts-expect-error Konva types are mismatched here.
Konva["Hoverable"] = Hoverable;

/**
 * Konva utiity for creating hoverable Rect elements.
 * This class extends the Konva Rect class and adds hover functionality.
 * @param hoverFill - The fill color to use when the element is hovered.
 * @param autoSize - Whether to automatically size the element to the parent.
 * @param hoverChange - A callback function to call when the hover state changes.
 * @param cursor - The cursor to use when the element is hovered.
 * @returns A new Hoverable instance.
 */
// @ts-expect-error Konva types are mismatched here.
export default "Hoverable" as KonvaNodeComponent<Konva.Rect, HoverableProps>;
